import { cleanPermalink } from '../../../statistics/lib/top-videos';
import type { PresentationInput } from '../presentation-input';
import { MONTHS_RU } from '../report-period';
import { preparePresentation } from './generate-pdf';
import { PAGE, POST } from './layout';
import { svgAssetsOf, svgInputOf } from './generate-svg';

/**
 * Выгрузка презентации в PowerPoint.
 *
 * ПОЧЕМУ КАРТИНКАМИ. Слайд рисуется по макету Figma: скруглённые карточки,
 * телефоны, экран статистики, точные межбуквенные интервалы. Повторить это
 * фигурами PowerPoint — значит завести третий рендер и чинить его каждый
 * раз при правке макета. Поэтому слайд приезжает картинкой, а PowerPoint
 * получает то, ради чего его и просили: возможность дособрать отчёт —
 * поменять порядок слайдов, добавить свои, отправить в общий доступ.
 *
 * Кликабельной остаётся кнопка «Посмотреть»: поверх неё лежит прозрачная
 * картинка со ссылкой на публикацию.
 */

/** Слайд 16:9 в дюймах — размер листа PowerPoint по умолчанию. */
const SLIDE_WIDTH = 13.333;
const SLIDE_HEIGHT = 7.5;
const INCH = SLIDE_WIDTH / PAGE.width;
/** Растр слайда: вдвое плотнее макета, чтобы текст не мылился на экране. */
const SCALE = 1.5;
const JPEG_QUALITY = 0.9;

const CLEAR_PIXEL =
  'image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';

async function rasterize(svg: string): Promise<string> {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(PAGE.width * SCALE);
    canvas.height = Math.round(PAGE.height * SCALE);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas недоступен');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function generatePresentationPptx(
  input: PresentationInput,
  onProgress: (message: string) => void = () => {},
): Promise<Blob> {
  const [{ jsPDF }, prepared] = await Promise.all([
    import('jspdf'),
    preparePresentation(input, onProgress),
  ]);

  onProgress('Сборка слайдов...');
  const { renderSlidesSvg } = await import('./render-svg');
  const slides = renderSlidesSvg(jsPDF, svgInputOf(input, prepared), svgAssetsOf(prepared.assets));

  const { default: PptxGenJS } = await import('pptxgenjs');
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.title = `Отчет ${MONTHS_RU[input.period.month]} ${input.period.year}`;

  const button = {
    x: POST.button.x * INCH,
    y: POST.button.y * (SLIDE_HEIGHT / PAGE.height),
    w: POST.button.w * INCH,
    h: POST.button.h * (SLIDE_HEIGHT / PAGE.height),
  };

  for (const [index, slide] of slides.entries()) {
    onProgress(`Слайд ${index + 1} из ${slides.length}...`);
    const data = await rasterize(slide.svg);
    const page = pptx.addSlide();
    page.addImage({ data, x: 0, y: 0, w: SLIDE_WIDTH, h: SLIDE_HEIGHT });
    // Слайды публикаций идут в том же порядке, что и сами публикации.
    const post = slide.name.startsWith('post-') ? input.posts[index - 1] : null;
    if (post?.url) {
      page.addImage({ data: CLEAR_PIXEL, ...button, hyperlink: { url: cleanPermalink(post.url) } });
    }
  }

  const blob = (await pptx.write({ outputType: 'blob' })) as Blob;
  return blob;
}
